import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGetRetiredObjectsQuery } from '../../api/inventory/inventoryApiSlice';
import { makeAssetDetailsUrl } from '../../routes';
import { STATION_TIME_ZONE } from '../../utils/stationTime';

interface RetiredObjectsListProps {
    searchQuery: string;
}

/**
 * The objects "Ausgemustert" shows in the Objekte overview (PO, 2026-10-15):
 * retired rooms and station objects, newest first. Every live list excludes
 * them; this is where an operator finds one again and, on its own page, puts
 * it back into operation.
 */
const RetiredObjectsList: React.FC<RetiredObjectsListProps> = ({ searchQuery }) => {
    const { t, i18n } = useTranslation();
    const { data: objects = [], isLoading, isError } = useGetRetiredObjectsQuery();
    const dateLocale = i18n.language.startsWith('de') ? 'de-DE' : 'en-US';

    const visible = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return objects;
        return objects.filter((object) =>
            [object.name, object.stationName ?? ''].some((text) => text.toLowerCase().includes(query)));
    }, [objects, searchQuery]);

    if (isLoading) {
        return <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>;
    }
    // A failed load is its own state, never an empty list.
    if (isError) {
        return <p className={'text-sm text-foreground/70'} role={'alert'}>{t('assets_retired.load_error')}</p>;
    }
    if (visible.length === 0) {
        return <p className={'text-sm text-foreground/60'}>{t('assets_retired.empty')}</p>;
    }

    return (
        <div className={'overflow-x-auto'}>
            <table className={'w-full text-sm'}>
                <thead>
                    <tr className={'border-b border-foreground/10'}>
                        <th className={'text-left py-2 text-foreground/60 font-medium'}>{t('assets_retired.columns.object')}</th>
                        <th className={'text-left py-2 text-foreground/60 font-medium'}>{t('assets_retired.columns.station')}</th>
                        <th className={'text-left py-2 text-foreground/60 font-medium'}>{t('assets_retired.columns.service')}</th>
                        <th className={'text-left py-2 text-foreground/60 font-medium'}>{t('assets_retired.columns.retired_at')}</th>
                    </tr>
                </thead>
                <tbody>
                    {visible.map((object) => (
                        <tr key={object.id} className={'border-b border-foreground/5'}>
                            <td className={'py-2'}>
                                <Link to={makeAssetDetailsUrl(object.id)} className={'text-primary hover:underline'}>
                                    {object.name}
                                </Link>
                            </td>
                            <td className={'py-2 text-foreground/80'}>
                                {object.stationName ?? t('common.not_applicable')}
                            </td>
                            <td className={'py-2 text-foreground/80'}>
                                {object.useCase
                                    ? t(`service_type_selector.${object.useCase}`, object.useCase)
                                    : t('common.not_applicable')}
                            </td>
                            <td className={'py-2 text-foreground/80'}>
                                {new Date(object.retiredAt).toLocaleDateString(dateLocale, {
                                    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: STATION_TIME_ZONE,
                                })}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default RetiredObjectsList;
